import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonModule } from 'primeng/button';

import { CountdownComponent } from '../countdown/countdown.component';
import { LogibidsNumberMaskDirective } from '../../../core/directives/number-mask.directive';
import { FitTextDirective } from '../../../core/directives/fit-text.directive';
import { assets } from '../../../core/constants/assets.const';
import {
  AuctionDateEntry,
  enquiry,
  getRefundedStatusTranslationKey,
} from '../../../models/enquiries';
import { User } from '../../../models/auth';
import { EnquiryService } from '../../../services/enquiries.service';
import { TranslatePipe } from '../../../core/pipes/translate.pipe';
import { I18nService } from '../../../services/i18n.service';
import { TruncateDecimalPipe } from '../../../core/pipes/TruncateDecimal.pipe';

/**
 * All derived read-only state for a single auction card.
 * Computed once per signal change via buildViewModel().
 * Zero function calls remain in the template — only property reads.
 */
export interface AuctionViewModel {
  readonly auction: enquiry;
  /** Resolved vehicle image (now delegated to enquiry.vehicleImage) */
  readonly vehicleImage: string;
  /** Result of getStatus() — used by template guards */
  readonly resolvedStatus: string;
  /** Translated status label for display */
  readonly statusLabel: string;
  /** 'myBid' | 'LostYourbid' | '' (delegated to enquiry.auctionOverlayStatus) */
  readonly auctionOverlayStatus: string;
  readonly isMyBid: boolean;
  readonly isLostBid: boolean;
  /** Pre-split schedule dates per location (delegated to enquiry.formattedDates) */
  readonly arrayDates: ReadonlyArray<AuctionDateEntry>;
  /** True when bid amount > 0 (delegated to enquiry.isAmountValid) */
  readonly isAmountValid: boolean;
  /** Memoised SVG mask for the cutout text effect */
  readonly textMask: SafeStyle;
  /** SP or driver with earnings flag enabled */
  readonly canShowEarnings: boolean;
  /** Show countdown in header (delivery / immediate + right status) */
  readonly showCountdownTimer: boolean;
  /** Show immediate countdown, hide for SHARED rides (delegated to enquiry.showImmediateCountdown) */
  readonly showImmediateCountdown: boolean;
  /** Show assigned driver details block */
  readonly showDriverDetails: boolean;
  /** Show price block for delivery / immediate trips (delegated to enquiry.isDeliveryOrImmediate) */
  readonly showDeliveryImmediatePrice: boolean;
  /** Show price block for expired or WON scheduled auctions */
  readonly showSchedulePrice: boolean;
  /** Direct driver bid is disabled for this SP */
  readonly isDirectBidDisabled: boolean;
  /** Show the "Last Bid" label above bid input */
  readonly showLastBidLabel: boolean;
  /** Current ISO timestamp for countdown [start] binding */
  readonly currentDateTime: string;
}

@Component({
  selector: 'logibids-enquiry-card',
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    ButtonModule,
    NgOptimizedImage,
    CountdownComponent,
    LogibidsNumberMaskDirective,
    FitTextDirective,
    TruncateDecimalPipe,
  ],
  templateUrl: './enquiry-card.component.html',
  styleUrl: './enquiry-card.component.scss',
})
export class EnquiryCardComponent {
  // ─── Inputs ───────────────────────────────────────────────────────────────────
  readonly auctionsInput = input.required<enquiry[]>({ alias: 'auctions' });
  readonly auctionTypeInput = input.required<string>({ alias: 'auctionType' });
  readonly userProfileInput = input.required<User>({ alias: 'userProfile' });
  readonly showSkipButton = input<boolean>(false);
  readonly source = input<string>(''); // Identifying the parent component

  // ─── Outputs ────────────────────────────────────────────────────────────────
  readonly bidPlaced = output<{ auction: enquiry; source: string }>();
  readonly auctionAccepted = output<{ auction: enquiry; source: string }>();
  readonly skipAuction = output<{ source: string }>();

  // ─── Public state (template reads) ───────────────────────────────────────────
  readonly assetsConst = assets;
  readonly isBlinking = signal<boolean>(false);

  onVehicleImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    const fallback = this.assetsConst.noVehicle;
    const currentSrc = img.getAttribute('src');
    if (currentSrc === fallback || img.src.endsWith(fallback)) {
      return;
    }
    img.src = fallback;
    img.classList.add('vehicle-fallback-image');
  }

  /**
   * Main derived list — one ViewModel per auction.
   * Profile-dependent flags are computed here; auction-only getters are
   * read directly from the enquiry model instances.
   */
  readonly auctionViewModels = computed<ReadonlyArray<AuctionViewModel>>(() => {
    const profile = this.userProfileInput();
    const auctionType = this.auctionTypeInput();
    return this.auctionsInput().map(a =>
      this.buildViewModel(a, profile, auctionType)
    );
  });

  // ─── Private deps ─────────────────────────────────────────────────────────────
  private readonly destroyRef = inject(DestroyRef);
  private readonly enquiryService = inject(EnquiryService);
  private readonly router = inject(Router);
  private readonly translateService = inject(I18nService);
  private readonly sanitizer = inject(DomSanitizer);

  private intervalId: ReturnType<typeof setInterval> | null = null;

  /** SVG mask memoisation — keyed by module_name, never re-encoded for the same text */
  private readonly textMaskCache = new Map<string, SafeStyle>();

  // ─── Lifecycle ────────────────────────────────────────────────────────────────
  constructor() {
    // afterNextRender: SSR-safe — interval only starts in the browser
    afterNextRender(() => {
      this.startBlinkingInterval();
    });

    this.destroyRef.onDestroy(() => {
      if (this.intervalId !== null) {
        clearInterval(this.intervalId);
      }
    });
  }

  // ─── Blinking interval ────────────────────────────────────────────────────────
  private startBlinkingInterval(): void {
    this.intervalId = setInterval(() => {
      this.isBlinking.set(true);
      setTimeout(() => {
        this.isBlinking.set(false);
      }, 2710);
    }, 10000);
  }

  // ─── ViewModel builder ────────────────────────────────────────────────────────
  /**
   * Computes the profile-dependent display flags for one auction.
   * Auction-only derived values (vehicleImage, formattedDates, etc.)
   * are read directly from enquiry model getters — no duplication.
   */
  private buildViewModel(
    auction: enquiry,
    profile: User,
    auctionType: string
  ): AuctionViewModel {
    // Resolve effective status once — drives multiple guard conditions below
    const resolvedStatus = this.getStatus(
      auction.status,
      auction.bid_status,
      auction.trip_status,
      auction.module_id,
      auction.driver_details?.driver_accepted ?? false,
      auction.schedule_type,
      profile
    );

    const canShowEarnings =
      profile.type === 'SERVICE_PROVIDER' ||
      (profile.type === 'DRIVER' && profile.driver?.allow_earnings === true);

    // Note: `auction.status !== 'CANCELLED'` removed — status cannot be both ENDED and CANCELLED
    const showDriverDetails =
      auction.status === 'ENDED' &&
      auction.trip_status !== 'CANCELLED' &&
      auction.driver_details !== null &&
      profile.type !== 'DRIVER';

    const showSchedulePrice =
      !auction.isDeliveryOrImmediate &&
      ((resolvedStatus !== 'RUNNING' && auction.auction_expired === true) ||
        resolvedStatus === 'WON');

    const isDirectBidDisabled =
      profile.type !== 'DRIVER' &&
      auction.schedule_direct_driver === true &&
      (auction.bid_user_id === null || auction.bid_user_id !== profile.id);

    const showSPBid =
      isDirectBidDisabled &&
      !!auction.last_bid &&
      auction.last_bid.sp_id?.toString() !== profile.id;

    const showLastBidLabel =
      (!isDirectBidDisabled &&
        !!auction.last_bid &&
        auction.last_bid.user_id?.toString() !== profile.id) ||
      showSPBid;

    return {
      auction,
      // ── Delegated to enquiry model getters ────────────────────────────────────
      vehicleImage: auction.vehicleImage,
      auctionOverlayStatus: auction.auctionOverlayStatus,
      isMyBid: auction.isMyBid,
      isLostBid: auction.isLostBid,
      arrayDates: auction.formattedDates,
      isAmountValid: auction.isAmountValid,
      showImmediateCountdown: auction.showImmediateCountdown,
      showDeliveryImmediatePrice: auction.isDeliveryOrImmediate,
      // ── Profile-dependent flags ────────────────────────────────────────────────
      resolvedStatus,
      statusLabel: this.statusTranslate(
        auction.status,
        auction.bid_status,
        auction.trip_status,
        auction.module_id,
        profile.type === 'DRIVER'
          ? (auction.driver_accepted ?? false)
          : (auction.driver_details?.driver_accepted ?? false),
        auction.schedule_type,
        profile,
        auction.refund_type
      ),
      textMask: this.getOrCreateTextMask(auction.module_name),
      canShowEarnings,
      showCountdownTimer:
        auction.isDeliveryOrImmediate &&
        (resolvedStatus === 'RUNNING' || resolvedStatus === 'PENDING') &&
        auction.status !== 'ACCEPTED',
      showDriverDetails,
      showSchedulePrice,
      isDirectBidDisabled,
      showLastBidLabel,
      currentDateTime: new Date().toISOString(),
    };
  }

  // ─── Memoised SVG text mask ───────────────────────────────────────────────────
  private getOrCreateTextMask(text: string): SafeStyle {
    if (this.textMaskCache.has(text)) return this.textMaskCache.get(text)!;
    const mask = this.generateTextMask(text);
    this.textMaskCache.set(text, mask);
    return mask;
  }

  private generateTextMask(text: string): SafeStyle {
    if (!text) return '';

    const escaped = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" width="300" height="60" viewBox="0 0 300 60">` +
      `<text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" ` +
      `fill="white" font-family="IBM Plex Serif" font-weight="bold" font-size="24">${escaped}</text>` +
      `</svg>`;

    return this.sanitizer.bypassSecurityTrustStyle(
      `url('data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}')`
    );
  }

  // ─── Status resolution ────────────────────────────────────────────────────────
  private getStatus(
    status: string,
    bidStatus: string,
    tripStatus: string,
    categoryRefId: string,
    driver: boolean,
    scheduleType: string,
    profile: User
  ): string {
    const isImmediateDelivery =
      categoryRefId === 'DELIVERY' ||
      (categoryRefId === 'TRANSPORT' && scheduleType === 'IMMEDIATE');

    switch (status) {
      case 'RUNNING':
        return isImmediateDelivery ? status : bidStatus;

      case 'ACCEPTED':
      case 'NOT-STARTED':
        return bidStatus;

      case 'CANCELLED':
        return 'TIMEOUT';

      case 'FAILED':
        return status;

      case 'ENDED': {
        const isSP = profile.type === 'SERVICE_PROVIDER';
        if (bidStatus === 'PENDING') return 'RUNNING';

        if (!isSP) {
          if (tripStatus === 'CANCELLED' || tripStatus === 'ENDED')
            return tripStatus;
          if (!driver) return 'ASSIGNED';
          if (driver && tripStatus === 'NOT-STARTED') return 'ACCEPTED';
          return tripStatus === 'NOT-STARTED'
            ? bidStatus || tripStatus
            : tripStatus;
        }

        if (bidStatus === 'RUNNING') return status;

        if (bidStatus === 'WON') {
          if (isImmediateDelivery) {
            return tripStatus === 'NOT-STARTED' ? 'ACCEPTED' : tripStatus;
          }
          if (tripStatus === 'NOT-STARTED') return bidStatus;
          return tripStatus;
        }

        return tripStatus === 'NOT-STARTED'
          ? bidStatus || tripStatus
          : tripStatus;
      }

      default:
        return status;
    }
  }

  private statusTranslate(
    status: string,
    bidStatus: string,
    tripStatus: string,
    categoryRefId: string,
    driver: boolean,
    scheduleType: string,
    profile: User,
    refundType?: enquiry['refund_type']
  ): string {
    const statusCheck = this.getStatus(
      status,
      bidStatus,
      tripStatus,
      categoryRefId,
      driver,
      scheduleType,
      profile
    );

    if (statusCheck === 'ENDED') {
      return this.translateService.translate(
        tripStatus === 'ENDED' ? 'trip_ended' : 'ended'
      );
    }

    const statusMap: Record<string, string> = {
      RUNNING: 'running',
      WON: 'won',
      CANCELLED: 'cancelled',
      PENDING: 'pending',
      'NOT-STARTED': 'not_started',
      LOST: 'lost',
      FAILED: 'failed',
      PAID: 'paid',
      DONE: 'done',
      ACCEPTED: 'accepted',
      'IN-PROCESS': 'in_process',
      STARTED: 'trip_started',
      ASSIGNED: 'ASSIGNED',
      ARRIVED: 'Arrived',
      ENDED: 'trip_ended',
      PAUSED: 'Trip_Paused',
      REFUNDED: getRefundedStatusTranslationKey(refundType),
      TIMEOUT: 'Timeout',
    };

    return this.translateService.translate(
      statusMap[statusCheck] ?? statusCheck
    );
  }

  // ─── Template event handlers ──────────────────────────────────────────────────
  timerDone(
    event: { hours: number; mins: number; secs: number },
    i: number
  ): void {
    const auction = this.auctionsInput()[i];
    if (event.hours === 0 && event.mins === 0 && event.secs === 0) {
      auction.stopBlinker = true;
      auction.auction_expired = true;
    } else {
      auction.stopBlinker = false;
    }
  }

  onBidChange(value: number | null, auction: enquiry): void {
    if (value === null || isNaN(value) || value === 0) {
      auction.bid_amount = null;
      return;
    }
    const tax = Number(auction.sp_cost?.tax_amount ?? 0);
    auction.bid_amount = parseFloat((value - tax).toFixed(3)).toString();
  }

  bidNow(bidAmount: string | null, auction: enquiry): void {
    if (!bidAmount) return;
    this.enquiryService
      .bidRequest({ bid_amount: Number(bidAmount) }, auction.id.toString())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.bidPlaced.emit({ auction, source: this.source() });
      });
  }

  acceptDelivery(auction: enquiry): void {
    this.enquiryService
      .bidDeliveryRequest(auction.id.toString())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        auction.status = 'ACCEPTED';
        this.auctionAccepted.emit({ auction, source: this.source() });
      });
  }

  skipNow(): void {
    this.skipAuction.emit({ source: this.source() });
  }

  goToEnquiry(auction: enquiry): void {
    const segment =
      this.auctionTypeInput() === 'schedule'
        ? '2'
        : this.auctionTypeInput() === 'immediate'
          ? '1'
          : '3';
    this.router.navigateByUrl(`/sp-bids/sp-new-bid/${auction.id}/${segment}`);
  }
}
